import { useEffect } from 'react';
import { PAGE_TYPE, CONTENT_CATEGORY, FORM_FIELD, EVENT, ADOBE_DATALAYER_EVENT, SITE_SECTION } from '@trip-ux/constants';
import { filter, get, map } from 'lodash';
import { computeTripDuration } from '../booking-coverme/trip-details/utils';
import { PRODUCT_TYPE } from '@trip-ux/redux-store';
import { sha256 } from 'js-sha256';

declare global {
  interface Window {
    CWC_MLCA: { dataLayer: { actions: unknown[] } };
    adobeDataLayer: unknown[];
  }
}

export interface PageLoadData {
  pageName: string;
  type: string;
  siteSection: string;
  domain: string;
  language: string;
  anonymousID: string;
  agencyCode: string;
  BDM: string;
  path: string;
  pageUrl: string;
  profileType: string;
}
export interface UserProfile {
  authId?: string;
  orgCode?: string;
  BDM?: string;
  profileType?: string;
}

export const useOnPageLoadAnalytics = (
  pageTitle: string,
  siteSection: string,
  locale: string,
  provinceValue: string,
  additionalDataLayers?
): void => {
  useEffect(() => {
    const analyticsObject = window.CWC_MLCA;
    if (analyticsObject) {
      analyticsObject.dataLayer.actions.push({
        event: EVENT.PAGE_LOAD,
        pageInfo: {
          name: pageTitle,
          site_section: siteSection,
          pageType: PAGE_TYPE,
          language: locale,
          province: provinceValue,
          contentCategory: CONTENT_CATEGORY,
          contentName: `${siteSection}:${pageTitle}`
        },
        ...additionalDataLayers
      });
    }
  }, []);
};

export const handleEventFormAnalytics = (eventTriggered: string, formName: string, formStep: string, planDetails?) => {
  const analyticsObject = window.CWC_MLCA;
  if (analyticsObject) {
    analyticsObject.dataLayer.actions.push({
      event: eventTriggered,
      form: {
        name: formName,
        formStep,
        type: FORM_FIELD.TYPE
      },
      ...planDetails
    });
  }
};

export const getPlanDetailsAnalytics = (
  quoteForm,
  travellersPlan,
  dateFormat,
  isMultiTrip,
  province,
  emailAddress,
  travellerCity?
) => {
  const travellers = get(quoteForm, 'travellers', []);
  const departureDate = get(quoteForm, 'departureDate');
  const returnDate = get(quoteForm, 'returnDate');
  const returnMultiTripDate = get(quoteForm, 'returnDateMultiTrip');
  const travellersPlanProducts = get(travellersPlan, 'products', []);
  return {
    plan: {
      planType: get(quoteForm, 'travellerType'),
      province,
      familyCoverage: get(quoteForm, 'isFamily'),
      travelerCount: get(quoteForm, 'travellerCount'),
      emailAddress,
      travelerAge: map(travellers, traveller => traveller.age),
      tripDestination: get(quoteForm, 'destination', ''),
      tripStart: departureDate,
      tripEnd: isMultiTrip === 'true'? returnMultiTripDate: returnDate,
      tripDuration:
        isMultiTrip === 'true'
          ? map(travellersPlanProducts, traveller => traveller.params.daysPerTrip)
          : computeTripDuration(departureDate, returnDate, dateFormat),
      tripType: isMultiTrip === 'true' ? 'Multi Trip' : 'Single Trip',
      travellersPlans: map(
        filter(travellersPlanProducts, travellerProduct => travellerProduct.type === PRODUCT_TYPE.PLAN),
        plan => ({
          id: plan.paxId,
          planCode: plan.code,
          deductible: get(plan.params, 'deductible', ''),
          tripCost: plan.total,
          coverageAmount: get(plan.params, 'coverageAmount', '')
        })
      ),
      addOn: map(
        filter(travellersPlanProducts, travellerProduct => travellerProduct.type === PRODUCT_TYPE.OPTIONAL_BENEFIT)
      ),
      ...(travellerCity && { travelerCity: travellerCity })
    }
  };
};

function ensureAdobeDataLayer() {
  if (typeof window !== 'undefined') {
    window.adobeDataLayer = window.adobeDataLayer || [];
    return true;
  }
  return false;
}
export const handleOnPageLoadAdobeDataLayer = (
  userProfile: UserProfile,
  locale: string,
  pageName: string,
  path: string,
  event: string,
  transactionStep: string,
  transactionValue: string
) => {
  const domain = window.location.origin;
  const authId = userProfile?.authId;
  const hashedId = sha256(authId ?? '');
  const pageUrl = window.location.href;

  const pageData = {
    pageName: pageName,
    type: 'Non-transactional',
    siteSection: SITE_SECTION.QUOTE_FORM,
    domain,
    language: locale,
    anonymousID: hashedId,
    agencyCode: userProfile.orgCode,
    BDM: userProfile.BDM,
    path,
    pageUrl,
    profileType: userProfile.profileType
  };
  addPageLoadAdobeDataLayer(pageData);
  handleContinueEventAdobeDataLayer(event, transactionStep, transactionValue);
};

export const addPageLoadAdobeDataLayer = ({
  pageName,
  type,
  siteSection,
  domain,
  language,
  anonymousID,
  agencyCode,
  BDM,
  path,
  pageUrl,
  profileType
}: PageLoadData) => {
  if (ensureAdobeDataLayer()) {
    window.adobeDataLayer.push({
      event: ADOBE_DATALAYER_EVENT.PAGE_LOAD,
      page: {
        pageName,
        type,
        siteType: 'N/A',
        siteSection,
        contentType: CONTENT_CATEGORY,
        brand: 'TRIPX',
        loB: 'Advisor',
        segment: 'CA',
        title: pageName,
        domain,
        path,
        pageURL: pageUrl,
        language
      },
      user: {
        anonymousID,
        userCode: agencyCode || '',
        BDMS: BDM || '',
        userType: profileType === 'ADVISOR' ? 'Advisor' : 'Agency'
      }
    });
  }
};

export const handleContinueEventAdobeDataLayer = (event: string, transactionStep: string, transactionValue: string) => {
  if (ensureAdobeDataLayer()) {
    window.adobeDataLayer.push({
      event: event,
      transaction: {
        transactionName: 'Quote',
        transactionID: '',
        transactionStep,
        transactionValue
      }
    });
  }
};

export const handleLinkClickAdobeDataLayer = (linkName: string, linkHref: string) => {
  if (ensureAdobeDataLayer()) {
    window.adobeDataLayer.push({
      event: ADOBE_DATALAYER_EVENT.LINK_CLICK,
      click: {
        linkName,
        linkHref
      }
    });
  }
};